import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const FIELDS = [
  { k: "site_name", label: "Site name" },
  { k: "tagline", label: "Tagline" },
  { k: "primary_phone", label: "Primary phone (Interventions & Coaching)" },
  { k: "secondary_phone", label: "Secondary phone" },
  { k: "evaluations_phone", label: "5329 Evaluations phone" },
  { k: "fax", label: "Fax" },
  { k: "primary_email", label: "Primary email" },
  { k: "evaluations_email", label: "Evaluations email" },
  { k: "facebook_url", label: "Facebook URL" },
];

const SEO_FIELDS = [
  { k: "site_url", label: "Site URL (canonical)", hint: "Public URL of your live site, e.g. https://spiritandassociates.org" },
  { k: "seo_keywords", label: "SEO keywords", hint: "Comma-separated. Used for meta keywords." },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);

  if (!s) return <div className="p-10 text-neutral-500">Loading…</div>;

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      delete payload.id;
      await api.patch("/settings", payload);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-10 max-w-3xl" data-testid="admin-settings">
      <p className="label-eyebrow mb-3">Configuration</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1a1a1a] mb-10">Site settings</h1>

      <div className="bg-white border border-neutral-200 p-8 space-y-5">
        {FIELDS.map(({ k, label }) => (
          <div key={k}>
            <Label>{label}</Label>
            <Input value={s[k] || ""} onChange={set(k)} data-testid={`settings-${k}`} />
          </div>
        ))}
        <div>
          <Label>Address</Label>
          <Textarea rows={2} value={s.address || ""} onChange={set("address")} />
        </div>
        <div>
          <Label>Footer text</Label>
          <Input value={s.footer_text || ""} onChange={set("footer_text")} />
        </div>
      </div>

      <div className="mt-10 bg-white border border-neutral-200 p-8 space-y-5" data-testid="seo-section">
        <div>
          <p className="label-eyebrow mb-2">Search engines</p>
          <h2 className="font-serif-display text-2xl text-[#1a1a1a]">SEO & social sharing</h2>
          <p className="text-sm text-neutral-500 mt-2">
            These fields control how Google, Facebook, and other platforms display your site. Page-level SEO descriptions are set on each page editor.
          </p>
        </div>
        {SEO_FIELDS.map(({ k, label, hint }) => (
          <div key={k}>
            <Label>{label}</Label>
            <Input value={s[k] || ""} onChange={set(k)} data-testid={`settings-${k}`} />
            {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
          </div>
        ))}
        <div>
          <Label>Default site description</Label>
          <Textarea
            rows={3}
            value={s.site_description || ""}
            onChange={set("site_description")}
            data-testid="settings-site_description"
            placeholder="150–160 characters describing your services. Used as default meta description."
          />
          <p className="text-xs text-neutral-500 mt-1">
            Shown in Google search results when a page has no specific SEO description. Aim for 150–160 characters.
          </p>
        </div>
        <div>
          <Label>Twitter / X handle</Label>
          <Input value={s.twitter_handle || ""} onChange={set("twitter_handle")} placeholder="@yourhandle" />
        </div>

        <div className="border-t border-neutral-200 pt-5 mt-2">
          <p className="label-eyebrow mb-2">Search engine verification</p>
          <p className="text-xs text-neutral-500 mb-4">
            Paste only the <code className="bg-neutral-100 px-1">content</code> value from the meta tag Google or Bing gives you (not the whole tag).
            Example: from <code className="bg-neutral-100 px-1">&lt;meta name="google-site-verification" content="abc123..." /&gt;</code> paste only <code className="bg-neutral-100 px-1">abc123...</code>
          </p>
          <div className="space-y-4">
            <div>
              <Label>Google Search Console verification code</Label>
              <Input
                value={s.google_site_verification || ""}
                onChange={set("google_site_verification")}
                data-testid="settings-google_site_verification"
                placeholder="e.g. _abcDEF123-xyz456_GHI789"
              />
            </div>
            <div>
              <Label>Bing Webmaster verification code</Label>
              <Input
                value={s.bing_site_verification || ""}
                onChange={set("bing_site_verification")}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        <div className="bg-neutral-50 border border-neutral-200 rounded p-4 text-sm text-neutral-700">
          <p className="font-semibold mb-1">Sitemap & robots</p>
          <p className="mb-2">Your sitemap is generated automatically. Submit one of these to Google Search Console:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs font-mono">
            <li>{(s.site_url || "https://spiritandassociates.org").replace(/\/$/, "")}/sitemap.xml <span className="text-neutral-500 font-sans">(static, main pages)</span></li>
            <li>{(s.site_url || "https://spiritandassociates.org").replace(/\/$/, "")}/api/sitemap.xml <span className="text-neutral-500 font-sans">(dynamic, includes blog posts)</span></li>
          </ul>
        </div>
      </div>

      <div className="mt-8">
        <button onClick={save} disabled={saving} className="btn-primary-burgundy disabled:opacity-60" data-testid="save-settings-btn">{saving ? "Saving..." : "Save settings"}</button>
      </div>
    </div>
  );
}
